import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMonthlyReport } from '../hook/useMonthlyReport.mjs';
import { AuthContext } from '../../authProvider.mjs';
import { secureLogger } from '../../utils/secureLogger.mjs';
import PageShell from '../../components/PageShell.mjs';

const months = [
  { value: 1, label: 'January' }, { value: 2, label: 'February' }, { value: 3, label: 'March' },
  { value: 4, label: 'April' }, { value: 5, label: 'May' }, { value: 6, label: 'June' },
  { value: 7, label: 'July' }, { value: 8, label: 'August' }, { value: 9, label: 'September' },
  { value: 10, label: 'October' }, { value: 11, label: 'November' }, { value: 12, label: 'December' }
];
const monthLabel = (m) => months.find(x => x.value === Number(m))?.label || m;

const MonthlyReportGenerator = () => {
  const navigate = useNavigate();
  const { generateReport, fetchEligibleClaims, loading, error, setError } = useMonthlyReport();
  const { user } = useContext(AuthContext);

  const [formData, setFormData] = useState({
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    region: user?.region || ''
  });

  // Review step: null = not loaded; array = eligible claims to review/defer.
  const [eligibleClaims, setEligibleClaims] = useState(null);
  const [includedIds, setIncludedIds] = useState(() => new Set());

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

  const handleInputChange = (field, value) =>
    setFormData(prev => ({ ...prev, [field]: value }));

  // Step 1 -> Step 2: load the claims eligible for this month.
  const handleReview = async (e) => {
    e.preventDefault();
    if (!formData.region) { setError('Please select your region'); return; }
    try {
      const claims = await fetchEligibleClaims(formData.month, formData.year, formData.region);
      setEligibleClaims(claims);
      setIncludedIds(new Set(claims.map(c => c.claimId))); // all included by default
    } catch (err) {
      secureLogger.error('Failed to load eligible claims', { status: err.response?.status });
    }
  };

  const toggleClaim = (claimId) => {
    setIncludedIds(prev => {
      const next = new Set(prev);
      if (next.has(claimId)) next.delete(claimId); else next.add(claimId);
      return next;
    });
  };

  const backToForm = () => { setEligibleClaims(null); setIncludedIds(new Set()); setError(null); };

  // Step 2 -> generate: deferred = eligible claims left unchecked.
  const handleGenerate = async () => {
    const excludeClaimIds = eligibleClaims
      .filter(c => !includedIds.has(c.claimId))
      .map(c => c.claimId);
    try {
      const newReport = await generateReport(formData.month, formData.year, formData.region, excludeClaimIds);
      navigate(`/monthly-report/${newReport._id}`);
    } catch (err) {
      secureLogger.error('Failed to generate report', { status: err.response?.status });
    }
  };

  const includedCount = includedIds.size;
  const deferredCount = (eligibleClaims?.length || 0) - includedCount;
  const includedTotal = (eligibleClaims || [])
    .filter(c => includedIds.has(c.claimId))
    .reduce((s, c) => s + (Number(c.amount) || 0), 0);

  return (
    <PageShell
      title="Generate Monthly Report"
      subtitle="Review approved claims and choose what to include this month"
      width="3xl"
    >
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2 mb-4">
            <span>!</span>{error}
          </div>
        )}

        {/* Step 1: pick period */}
        {eligibleClaims === null && (
          <div className="bg-white rounded-xl shadow-lg p-4 sm:p-8">
            <form onSubmit={handleReview} className="space-y-4 sm:space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label htmlFor="month" className="block text-sm font-medium text-gray-700 mb-1">Month</label>
                  <select id="month" value={formData.month}
                    onChange={(e) => handleInputChange('month', parseInt(e.target.value))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-stanford-red focus:border-transparent">
                    {months.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                  </select>
                </div>
                <div>
                  <label htmlFor="year" className="block text-sm font-medium text-gray-700 mb-1">Year</label>
                  <select id="year" value={formData.year}
                    onChange={(e) => handleInputChange('year', parseInt(e.target.value))}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-stanford-red focus:border-transparent">
                    {years.map(y => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
                <div>
                  <label htmlFor="region" className="block text-sm font-medium text-gray-700 mb-1">Region</label>
                  <select id="region" value={formData.region} disabled required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-100 cursor-not-allowed">
                    <option value={user?.region}>{user?.region}</option>
                  </select>
                </div>
              </div>

              <div className="flex flex-col-reverse sm:flex-row gap-3 sm:gap-4 pt-2 sm:pt-4">
                <button type="button" onClick={() => navigate('/dashboard/monthly-reports')}
                  className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors">
                  Cancel
                </button>
                <button type="submit" disabled={loading || !formData.region}
                  className="flex-1 px-6 py-3 bg-stanford-red text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                  {loading ? 'Loading claims…' : 'Review Claims'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Step 2: review + defer */}
        {eligibleClaims !== null && (
          <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6">
            <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 sm:gap-3 mb-4">
              <h2 className="text-lg font-semibold text-gray-800">
                {monthLabel(formData.month)} {formData.year} - {formData.region}
              </h2>
              <div className="text-sm text-gray-600">
                <span className="font-medium text-gray-800">{includedCount}</span> included ·{' '}
                <span className="font-medium text-amber-700">{deferredCount}</span> deferred ·{' '}
                <span className="font-medium text-gray-800">${includedTotal.toFixed(2)}</span>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 text-amber-800 text-sm rounded-lg px-4 py-3 mb-4">
              Unchecked claims are <strong>deferred</strong> they stay open and reappear in a later
              month's report (e.g. reimbursed next cycle). They are not removed.
            </div>

            {eligibleClaims.length === 0 ? (
              <div className="text-center text-gray-500 py-10">
                No eligible claims for {monthLabel(formData.month)} {formData.year}.
              </div>
            ) : (
              <div className="overflow-x-auto border border-gray-200 rounded-lg">
                <table className="w-full text-sm responsive-table mrg-eligible-table">
                  <thead className="bg-gray-50 text-gray-600">
                    <tr>
                      <th className="w-10 px-3 py-2"></th>
                      <th className="text-left px-3 py-2">Employee</th>
                      <th className="text-left px-3 py-2">Type</th>
                      <th className="text-right px-3 py-2">Amount (USD)</th>
                      <th className="text-left px-3 py-2">Submitted</th>
                    </tr>
                  </thead>
                  <tbody>
                    {eligibleClaims.map(c => {
                      const included = includedIds.has(c.claimId);
                      return (
                        <tr key={c.claimId}
                          className={`md:border-t border-gray-100 last:border-b-0 ${included ? '' : 'bg-amber-50/60 text-gray-400'}`}>
                          {/* On phones the label fills the card's left column, so
                              the whole strip is the tap target; inline at md+. */}
                          <td className="px-3 py-2 text-center" data-cell="check">
                            <label className="flex justify-center w-full pt-1.5 cursor-pointer md:inline md:w-auto md:pt-0">
                              <input type="checkbox" checked={included}
                                onChange={() => toggleClaim(c.claimId)}
                                aria-label={`Include ${c.userName || 'claim'} in this report`}
                                className="h-5 w-5 md:h-4 md:w-4 accent-stanford-red cursor-pointer" />
                            </label>
                          </td>
                          <td className="px-3 py-2 break-words" data-cell="title">{c.userName}</td>
                          <td className="px-3 py-2" data-label="Type">{c.claimType === 'PerDiemClaim' ? 'Per Diem' : 'Expense'}</td>
                          <td className="px-3 py-2 text-right tabular-nums" data-label="Amount (USD)">${(Number(c.amount) || 0).toFixed(2)}</td>
                          <td className="px-3 py-2" data-label="Submitted">{c.createdAt ? new Date(c.createdAt).toLocaleDateString() : '-'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            <div className="flex flex-col-reverse sm:flex-row gap-3 sm:gap-4 pt-4 sm:pt-6">
              <button type="button" onClick={backToForm}
                className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors">
                Back
              </button>
              <button type="button" onClick={handleGenerate} disabled={loading || includedCount === 0}
                className="flex-1 px-6 py-3 bg-stanford-red text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                {loading ? 'Generating…' : `Generate Report (${includedCount})`}
              </button>
            </div>
          </div>
        )}
    </PageShell>
  );
};

export default MonthlyReportGenerator;
